// features/ups/modals/AgencyDrawer.js
// Right-side slide-over showing an agency’s contextual details (Accounts tab now; Settings placeholder)

import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { X, MoreHorizontal } from 'lucide-react';
import { fetchAccounts, deleteAccount as deleteAccountThunk } from '../../agencyAccounts/agencyAccountsSlice';

// Small inline kebab menu
function RowMenu({ onDelete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, []);
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="inline-flex items-center justify-center h-9 w-9 rounded hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <MoreHorizontal size={18} />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 mt-1 w-40 rounded-md border bg-white shadow-lg overflow-hidden z-50">
          <button
            role="menuitem"
            onClick={() => { setOpen(false); onDelete?.(); }}
            className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50"
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
}

export default function AgencyDrawer({ isOpen, onClose, agency, onAddAccount }) {
  const dispatch = useDispatch();
  const { list, loading, error } = useSelector(s => s.agencyAccounts);

  // ✅ Hooks belong inside the component
  const [tab, setTab] = useState('accounts');

  const agId = agency?.id;
  const agName = agency?.name;

  // Fetch (server may or may not filter by agencyId; we’ll also filter client-side)
  useEffect(() => {
    if (!isOpen) return;
    if (!agId && !agName) return;
    dispatch(fetchAccounts({ agencyId: agId }));
  }, [dispatch, isOpen, agId, agName]);

  // Client-side filter to ensure only this agency’s accounts show
  let rows = list || [];
  if (agId || agName) {
    const idStr = agId != null && agId !== '' ? String(agId) : null;
    const nameStr = agName ? agName.trim().toLowerCase() : null;
    rows = rows.filter(acc => {
      const accId = acc.agencyId ?? acc.agency_id ?? acc.agency?.id;
      const accName = (acc.agency_name ?? acc.agency?.name ?? '').trim().toLowerCase();
      if (idStr && accId != null && String(accId) === idStr) return true;
      if (nameStr && accName && accName === nameStr) return true;
      return false;
    });
    // Hide UPS roles in agency-scoped view
    rows = rows.filter(acc => acc.role?.startsWith('agency'));
  }

  if (!isOpen) return null;

  return (
    <div className="absolute inset-0 z-40">
      <div className="absolute inset-0 bg-black/20" onClick={onClose} />
      <aside className="absolute right-0 top-0 h-full w-full sm:w-[520px] md:w-[640px] bg-white shadow-xl flex flex-col">
        {/* Header (sticky) */}
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white">
          <h3 className="text-lg font-semibold truncate">{agency?.name}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-4 pt-3">
          <div className="flex gap-2 mb-3">
            <button
              onClick={() => setTab('accounts')}
              className={`px-3 py-1.5 rounded text-sm ${tab==='accounts' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
            >
              Accounts
            </button>
            <button
              onClick={() => setTab('settings')}
              className={`px-3 py-1.5 rounded text-sm ${tab==='settings' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
            >
              Settings
            </button>
          </div>
        </div>

        {/* Content (scrollable) */}
        <div className="px-4 pb-20 overflow-y-auto flex-1">
          {tab === 'accounts' && (
            <>
              {loading && <div className="text-gray-500 p-3">Loading…</div>}
              {error && <div className="text-red-600 p-3">{error}</div>}

              {!loading && !error && rows.length === 0 && (
                <div className="text-gray-500 p-3">No accounts for this agency</div>
              )}

              {!loading && !error && rows.length > 0 && (
                <div className="divide-y">
                  {rows.map(acc => (
                    <div key={acc.id} className="flex items-center justify-between py-3">
                      <div className="min-w-0">
                        <div className="font-medium truncate">{acc.username}</div>
                        <div className="text-sm text-gray-600 truncate">{acc.email}</div>
                      </div>
                      <div className="ml-3 flex items-center gap-2">
                        <span className="text-xs px-2 py-1 rounded bg-gray-100 whitespace-nowrap">{acc.role}</span>
                        <RowMenu onDelete={() => dispatch(deleteAccountThunk(acc.id))} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {tab === 'settings' && (
            <div className="text-gray-500 p-3">Settings coming soon…</div>
          )}
        </div>

        {/* Sticky footer action */}
        <div className="p-3 border-t bg-white sticky bottom-0">
          <button
            onClick={onAddAccount}
            className="w-full px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Add account
          </button>
        </div>
      </aside>
    </div>
  );
}
